import { CircularProgress } from '@mui/material';
import {
  getMinorNightWorkDefaultsRequest,
  minorNightWorkExceptionRequests,
} from 'api/requests/contract-compliance-requests';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import { MinorNightWorkRule } from 'constants/MinorNightWorkRule';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__EXCEPTION_INTERVAL_HINT,
  CONTRACT_COMPLIANCE__NIGHT_WORK_INTRO,
  GENERAL__RETRY,
} from 'translations/constants';
import { IRuleException } from 'types/contract-compliance/IContractCompliance';
import ComplianceToasts from '../ComplianceToasts';
import { useRuleDefaultPanel } from '../hooks/useRuleDefaultPanel';
import { useRuleDefaults } from '../hooks/useRuleDefaults';
import { useRuleExceptionPanel } from '../hooks/useRuleExceptionPanel';
import { useRuleExceptions } from '../hooks/useRuleExceptions';
import { useToasts } from '../hooks/useToasts';
import IntervalFields from '../IntervalFields';
import PanelConfirmModals from '../PanelConfirmModals';
import RuleCard from '../RuleCard';
import RuleExceptionPanel from '../RuleExceptionPanel';
import { HintText, PanelErrorRow, SubAreaContainer } from '../styles';
import { RULE_NOTE_KEYS, RULE_SLUGS, RULE_TITLE_KEYS, RULES } from './constants';
import { minorNightWorkDefaultAdapter } from './defaultAdapter';
import NightWorkDefaultPanel from './NightWorkDefaultPanel';
import { formatNightWorkDefault, formatNightWorkExceptionValue } from './utils';

const TEST_ID = 'minor-night-work';

const NightWorkForMinors = (): JSX.Element => {
  const [t] = useTranslation();
  const defaults = useRuleDefaults(getMinorNightWorkDefaultsRequest);
  const absoluteProhibitionExceptions = useRuleExceptions(
    MinorNightWorkRule.ABSOLUTE_PROHIBITION,
    minorNightWorkExceptionRequests,
  );
  const under16Exceptions = useRuleExceptions(MinorNightWorkRule.UNDER_16, minorNightWorkExceptionRequests);
  const age1617Exceptions = useRuleExceptions(MinorNightWorkRule.AGE_16_17, minorNightWorkExceptionRequests);
  const exceptionsByRule = {
    [MinorNightWorkRule.ABSOLUTE_PROHIBITION]: absoluteProhibitionExceptions,
    [MinorNightWorkRule.UNDER_16]: under16Exceptions,
    [MinorNightWorkRule.AGE_16_17]: age1617Exceptions,
  };
  const { toasts, pushToast, dismissToast, pushRetryToast } = useToasts();

  const { reload: reloadDefaults } = defaults;
  const { reload: reloadAbsoluteProhibition } = absoluteProhibitionExceptions;
  const { reload: reloadUnder16 } = under16Exceptions;
  const { reload: reloadAge1617 } = age1617Exceptions;
  const reloadAll = useCallback(() => {
    reloadDefaults();
    reloadAbsoluteProhibition();
    reloadUnder16();
    reloadAge1617();
  }, [reloadDefaults, reloadAbsoluteProhibition, reloadUnder16, reloadAge1617]);

  const panelCallbacks = {
    onSaved: (messageKey: string) => {
      reloadAll();
      pushToast('success', t(messageKey));
    },
    onClosed: reloadAll,
    onGone: (message: string) => {
      reloadAll();
      pushToast('error', message);
    },
    onStale: reloadAll,
    onError: (message: string) => pushToast('error', message),
  };
  const defaultPanel = useRuleDefaultPanel(minorNightWorkDefaultAdapter, panelCallbacks);
  const exceptionPanel = useRuleExceptionPanel(minorNightWorkExceptionRequests, true, {
    ...panelCallbacks,
    onRemoveFailed: (message: string, retry: () => void) => pushRetryToast(message, retry, `${TEST_ID}-remove-retry`),
  });

  const { open: openDefault } = defaultPanel;
  const { openAdd, openEdit } = exceptionPanel;
  const onEditException = useCallback((row: IRuleException) => openEdit(row), [openEdit]);
  const formatValue = useCallback((row: IRuleException) => formatNightWorkExceptionValue(t, row), [t]);

  const renderBody = () => {
    if (defaults.loadErrorMessage && !defaults.hasLoaded) {
      return (
        <PanelErrorRow data-testid={`${TEST_ID}-load-error`}>
          <ErrorBar message={defaults.loadErrorMessage} />
          <UPButton text={t(GENERAL__RETRY)} onClick={defaults.reload} outlined testId={`${TEST_ID}-load-retry`} />
        </PanelErrorRow>
      );
    }
    if (!defaults.hasLoaded) return <CircularProgress data-testid={`${TEST_ID}-loading`} />;
    return RULES.map(rule => {
      const value = defaults.defaults[rule];
      return (
        <RuleCard
          key={rule}
          title={t(RULE_TITLE_KEYS[rule])}
          note={t(RULE_NOTE_KEYS[rule])}
          defaultText={value ? formatNightWorkDefault(t, value) : undefined}
          testIdPrefix={`${TEST_ID}-${RULE_SLUGS[rule]}`}
          exceptions={exceptionsByRule[rule]}
          onEditDefault={() => openDefault(rule)}
          onAddException={() => openAdd(rule)}
          onEditException={onEditException}
          formatValue={formatValue}
        />
      );
    });
  };

  return (
    <SubAreaContainer data-testid={TEST_ID}>
      <HintText data-testid={`${TEST_ID}-intro`}>{t(CONTRACT_COMPLIANCE__NIGHT_WORK_INTRO)}</HintText>
      {renderBody()}

      <NightWorkDefaultPanel panel={defaultPanel} />
      <RuleExceptionPanel
        panel={exceptionPanel}
        title={exceptionPanel.rule ? t(RULE_TITLE_KEYS[exceptionPanel.rule as MinorNightWorkRule]) : ''}
        testIdPrefix={`${TEST_ID}-exception`}
        hintKey={CONTRACT_COMPLIANCE__EXCEPTION_INTERVAL_HINT}
        valueFields={
          <IntervalFields
            value={exceptionPanel.interval}
            onChange={exceptionPanel.setIntervalField}
            errors={exceptionPanel.intervalErrors}
            disabled={exceptionPanel.isSaving}
            testIdPrefix={`${TEST_ID}-exception`}
          />
        }
      />
      <PanelConfirmModals defaultPanel={defaultPanel} exceptionPanel={exceptionPanel} testIdPrefix={TEST_ID} />
      <ComplianceToasts toasts={toasts} onDismiss={dismissToast} />
    </SubAreaContainer>
  );
};

export default NightWorkForMinors;
